import React, { useEffect, useRef, useState } from 'react';
import { adminService } from '../../services';
import { useUser } from '../../context/UserContext';
// Leaves only, never the `./shell` barrel: the person drawer mounts this and AdminShell mounts
// the drawer, so the barrel would be a cycle.
import { AdminModal, Avatar, Btn, Field, FieldLabel, Pill, SegTrack, inputClass, money, signedMoney, textareaClass } from './shell/ui';
import { ActionIcon } from './shell/icons';
import { useAdminShell } from './shell/AdminShellContext';

const REASON_MAX = 500;
const CONFIRM_WORD = 'CONFIRM';

/** "12.5" → 1250; anything that is not a number that is ≥ 0 → NaN. */
export const toCents = (text) => {
  const t = String(text ?? '').trim().replace(/^\$/, '');
  if (t === '' || !/^\d*(\.\d{0,2})?$/.test(t)) return NaN;
  return Math.round(parseFloat(t) * 100);
};

/**
 * What a move will do, worked out from the balances on screen: the confirm step lists it and
 * the tests read it. `null` while something is missing. The server re-reads the balances, so
 * the DONE step prints what actually happened.
 */
export function planMove({ mode, direction = 'add', amountCents, setToCents, person, recipient }) {
  const bal = person?.balance ?? 0;
  if (mode === 'transfer') {
    if (!recipient || !(amountCents > 0)) return null;
    return {
      amount: amountCents,
      from: { before: bal, after: bal - amountCents },
      to: { before: recipient.balance ?? 0, after: (recipient.balance ?? 0) + amountCents },
    };
  }
  if (mode === 'set') {
    if (!(setToCents >= 0) || setToCents === bal) return null;
    return { amount: setToCents - bal, from: { before: bal, after: setToCents } };
  }
  if (!(amountCents > 0)) return null;
  const amount = direction === 'remove' ? -amountCents : amountCents;
  return { amount, from: { before: bal, after: bal + amount } };
}

/** What is still missing before REVIEW can go, in words. */
export function missingFor({ mode, plan, reason, person, recipient, amountCents, setToCents }) {
  const out = [];
  if (mode === 'transfer' && !recipient) out.push('who it goes to');
  if (mode === 'set' && !(setToCents >= 0)) out.push('the new balance');
  else if (mode === 'set' && setToCents === (person?.balance ?? 0)) out.push('a balance different from today’s');
  if (mode !== 'set' && !(amountCents > 0)) out.push('an amount above $0.00');
  if (mode === 'transfer' && amountCents > (person?.balance ?? 0)) out.push(`no more than the ${money(person?.balance ?? 0)} available`);
  if (!reason.trim()) out.push('a reason');
  if (out.length === 0 && !plan) out.push('an amount');
  return out;
}

/**
 * WALLET (Oct 28 2026): ADJUST · SET BALANCE · TRANSFER on one person's wallet, from the
 * USERS row, the HOSTS row and the person drawer. ADJUST and SET BALANCE are `wallets ·
 * adjust`; TRANSFER is superadmin only — a host who wants paying outside the app has their
 * available balance moved to a house account or another host. Every move needs a reason,
 * then a confirm step that lists what will happen and asks for CONFIRM typed out. Escrow is
 * never touched.
 *
 * person: { id, name, email, handle, avatarUrl, balance, escrow }
 */
export default function WalletMoveModal({ person, initialMode = 'adjust', onClose, onDone }) {
  const { isSuperadmin } = useUser();
  const { toast } = useAdminShell();
  const [mode, setMode] = useState(initialMode);
  const [direction, setDirection] = useState('add');
  const [amount, setAmount] = useState('');
  const [setTo, setSetTo] = useState('');
  const [recipient, setRecipient] = useState(null);
  const [reason, setReason] = useState('');
  const [step, setStep] = useState('form');   // form → confirm → done
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [missing, setMissing] = useState([]);
  const [result, setResult] = useState(null);

  // Start clean each time it opens for someone — keyed on who and how, not on the person
  // object, which a parent re-render recreates mid-typing.
  const openKey = person ? `${person.id}:${initialMode}` : '';
  useEffect(() => {
    if (!openKey) return;
    setMode(initialMode === 'transfer' && !isSuperadmin ? 'adjust' : initialMode);
    setDirection('add'); setAmount(''); setSetTo(''); setRecipient(null); setReason('');
    setStep('form'); setTyped(''); setBusy(false); setError(''); setMissing([]); setResult(null);
  }, [openKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!person) return null;

  const amountCents = toCents(amount);
  const setToCents = toCents(setTo);
  const plan = planMove({ mode, direction, amountCents, setToCents, person, recipient });
  const gaps = missingFor({ mode, plan, reason, person, recipient, amountCents, setToCents });
  const modes = [
    { value: 'adjust', label: 'ADJUST' },
    { value: 'set', label: 'SET BALANCE' },
    isSuperadmin && { value: 'transfer', label: 'TRANSFER' },
  ].filter(Boolean);

  const review = () => {
    if (gaps.length) { setMissing(gaps); return; }
    setMissing([]); setError(''); setTyped(''); setStep('confirm');
  };

  const run = async () => {
    if (typed.trim() !== CONFIRM_WORD || !plan) return;
    setBusy(true); setError('');
    const why = reason.trim();
    const r = mode === 'transfer'
      ? await adminService.transferWallet(person.id, recipient.id, plan.amount, why)
      : mode === 'set'
        ? await adminService.adjustWallet(person.id, 0, why, setToCents)
        : await adminService.adjustWallet(person.id, plan.amount, why);
    setBusy(false);
    if (!r.success) { setError(r.error || 'Nothing moved. The server refused it.'); return; }
    setResult(r.data);
    setStep('done');
    toast?.(mode === 'transfer' ? `MOVED ${money(plan.amount)} TO ${(recipient.name || recipient.email).toUpperCase()}` : `WALLET ${mode === 'set' ? 'SET' : 'ADJUSTED'}`);
    onDone?.(person.id, mode === 'transfer' ? recipient.id : null);
  };

  const title = step === 'confirm' ? 'CONFIRM THIS MOVE' : step === 'done' ? 'DONE' : 'WALLET';
  const kicker = step === 'form'
    ? 'Available balance only — escrow belongs to events until it is released. Every move is written to the ledger and the audit log under your name.'
    : step === 'confirm' ? 'Read it through. Nothing has moved yet.' : 'This is what the server did.';

  let footer;
  if (step === 'form') {
    footer = <><Btn size="sheet" onClick={onClose}>CANCEL</Btn><Btn size="sheet" variant="solid" onClick={review} data-testid="wallet-review">REVIEW</Btn></>;
  } else if (step === 'confirm') {
    const armed = typed.trim() === CONFIRM_WORD;
    footer = (
      <>
        <Btn size="sheet" onClick={() => { setStep('form'); setError(''); }} disabled={busy}>BACK</Btn>
        <Btn size="sheet" variant={plan && plan.amount < 0 && mode !== 'transfer' ? 'red' : 'solid'} onClick={run} disabled={!armed || busy} data-testid="wallet-run">
          {busy ? 'MOVING…' : mode === 'transfer' ? `TRANSFER ${money(plan?.amount)}` : mode === 'set' ? `SET TO ${money(setToCents)}` : `${plan?.amount < 0 ? 'REMOVE' : 'ADD'} ${money(Math.abs(plan?.amount || 0))}`}
        </Btn>
      </>
    );
  } else {
    footer = <Btn size="sheet" variant="solid" onClick={onClose}>CLOSE</Btn>;
  }

  return (
    <AdminModal open onClose={busy ? undefined : onClose} title={title} kicker={kicker} width={560} footer={footer}>
      <div className="flex flex-col gap-4" data-testid="wallet-move-modal">
        <PersonStrip person={person} label={mode === 'transfer' ? 'FROM' : 'WALLET'} />

        {step === 'form' && (
          <>
            {modes.length > 1 && (
              <SegTrack ariaLabel="What to do" value={mode} onChange={(m) => { setMode(m); setMissing([]); }} options={modes} className="!bg-[#F2F2F2] self-start" />
            )}

            {mode === 'adjust' && (
              <div className="flex flex-col gap-3">
                <SegTrack ariaLabel="Add or remove" size="sm" value={direction} onChange={setDirection} options={[{ value: 'add', label: '+ ADD' }, { value: 'remove', label: '− REMOVE' }]} className="!bg-[#F2F2F2] self-start" />
                <MoneyField label="AMOUNT" value={amount} onChange={setAmount} />
              </div>
            )}

            {mode === 'set' && (
              <div className="flex flex-col gap-2">
                <MoneyField label="NEW AVAILABLE BALANCE" value={setTo} onChange={setSetTo} placeholder={(person.balance / 100).toFixed(2)} />
                {plan && <FieldLabel>THAT IS {signedMoney(plan.amount)} ON TODAY’S {money(person.balance)}.</FieldLabel>}
              </div>
            )}

            {mode === 'transfer' && (
              <div className="flex flex-col gap-3">
                {recipient
                  ? <PersonStrip person={recipient} label="TO" onClear={() => setRecipient(null)} />
                  : <Field label="TO"><PersonSearch excludeId={person.id} onPick={setRecipient} /></Field>}
                <MoneyField
                  label="AMOUNT"
                  value={amount}
                  onChange={setAmount}
                  action={person.balance > 0 ? <button type="button" onClick={() => setAmount((person.balance / 100).toFixed(2))} className="h-9 flex-shrink-0 rounded-full bg-black px-4 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-white hover:opacity-85">ALL {money(person.balance)}</button> : null}
                />
              </div>
            )}

            <Field label={`REASON · REQUIRED · ${reason.length}/${REASON_MAX}`}>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
                rows={3}
                placeholder={mode === 'transfer' ? 'e.g. Dave was paid by e-transfer for Oct 24; balance moves to the house account.' : 'Why this balance is changing'}
                className={textareaClass}
                aria-label="Reason"
              />
            </Field>

            {missing.length > 0 && (
              <p className="m-0 rounded-[18px] bg-[#FFF0F8] px-4 py-3 font-mono text-[11px] leading-[1.6] text-[#B0006F]" role="alert">
                STILL NEEDS {missing.join(' · ').toUpperCase()}.
              </p>
            )}
          </>
        )}

        {step === 'confirm' && plan && (
          <>
            <ConfirmList mode={mode} plan={plan} person={person} recipient={recipient} reason={reason.trim()} />
            <Field label={`TYPE ${CONFIRM_WORD} TO MOVE THE MONEY`} error={error}>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
                placeholder={CONFIRM_WORD}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                className={`${inputClass} uppercase tracking-[0.2em]`}
                aria-label={`Type ${CONFIRM_WORD}`}
                autoFocus
              />
            </Field>
          </>
        )}

        {step === 'done' && result && <DoneList mode={mode} result={result} person={person} recipient={recipient} />}
      </div>
    </AdminModal>
  );
}

function MoneyField({ label, value, onChange, placeholder = '0.00', action }) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <span className="pointer-events-none absolute left-[18px] top-1/2 -translate-y-1/2 font-mono text-[13px] font-bold">$</span>
          <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder={placeholder}
            className={`${inputClass} pl-[32px] font-bold`}
            aria-label={label}
          />
        </div>
        {action}
      </div>
    </Field>
  );
}

function PersonStrip({ person, label, onClear }) {
  const name = person.name || person.displayName || person.email;
  return (
    <div className="flex items-center gap-3 rounded-[20px] bg-[#F7F7F7] px-4 py-3">
      <Avatar url={person.avatarUrl} name={name} size={40} />
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <span className="font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]">{label}</span>
        <span className="truncate font-mono text-[13px] font-bold">{name}</span>
        <span className="truncate font-mono text-[10px] text-[#6B7280]">{person.handle ? `@${person.handle} · ` : ''}{person.email}</span>
      </div>
      <div className="flex flex-shrink-0 flex-col items-end gap-1">
        <Pill tone="dark">{money(person.balance ?? 0)}</Pill>
        {(person.escrow ?? 0) > 0 && <span className="font-mono text-[9px] font-bold uppercase tracking-[0.1em] text-[#9CA3AF]">+{money(person.escrow)} ESCROW</span>}
      </div>
      {onClear && (
        <button type="button" onClick={onClear} aria-label="Pick someone else" className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white font-mono text-[12px] font-bold hover:bg-black hover:text-white">✕</button>
      )}
    </div>
  );
}

/** The transfer's search-select: name, email or @handle → up to 12 people, a tap picks one. */
export function PersonSearch({ excludeId, onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('idle');   // idle · loading · done · error
  const seq = useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (term.replace(/^@/, '').length < 2) { setRows([]); setState('idle'); return undefined; }
    const mine = ++seq.current;
    setState('loading');
    const t = setTimeout(async () => {
      const r = await adminService.searchWalletPeople(term, excludeId);
      if (mine !== seq.current) return;
      if (r.success) { setRows(r.data?.people || []); setState('done'); } else { setRows([]); setState('error'); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, excludeId]);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <span className="pointer-events-none absolute left-[18px] top-1/2 -translate-y-1/2 text-[#9CA3AF]"><ActionIcon name="person" size={16} /></span>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="NAME, EMAIL OR @HANDLE"
          className={`${inputClass} pl-[44px] uppercase tracking-[0.08em]`}
          aria-label="Find who it goes to"
          autoFocus
        />
      </div>
      {state === 'loading' && rows.length === 0 && <span className="px-2 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-[#9CA3AF]">LOOKING…</span>}
      {state === 'done' && rows.length === 0 && <span className="px-2 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-[#9CA3AF]">NOBODY MATCHES.</span>}
      {state === 'error' && <span className="px-2 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-[#DC2626]">COULD NOT SEARCH.</span>}
      {rows.length > 0 && (
        <ul role="listbox" aria-label="People" className="m-0 flex max-h-[264px] list-none flex-col gap-1 overflow-y-auto rounded-[20px] bg-[#F7F7F7] p-1">
          {rows.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => onPick({ ...p, name: p.displayName || p.email })}
                className="flex w-full items-center gap-3 rounded-[16px] px-3 py-[9px] text-left transition-colors duration-200 hover:bg-white"
              >
                <Avatar url={p.avatarUrl} name={p.displayName || p.email} size={32} />
                <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
                  <span className="truncate font-mono text-[12px] font-bold">{p.displayName || p.email}{p.isTestAccount ? <span className="ml-2 text-[9px] text-[#9CA3AF]">DEMO</span> : null}</span>
                  <span className="truncate font-mono text-[10px] text-[#6B7280]">{p.handle ? `@${p.handle} · ` : ''}{p.email}</span>
                </span>
                <span className="flex-shrink-0 rounded-full bg-white px-3 py-[5px] font-mono text-[11px] font-extrabold">{money(p.balance)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-[#F2F2F2] py-[9px] last:border-0">
      <span className="flex-shrink-0 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]">{label}</span>
      <span className="min-w-0 text-right font-mono text-[12px] font-bold">{children}</span>
    </div>
  );
}

const before2after = (a, b) => <>{money(a)} <span className="text-[#9CA3AF]">→</span> <span className={b < 0 ? 'text-[#DC2626]' : ''}>{money(b)}</span></>;

function ConfirmList({ mode, plan, person, recipient, reason }) {
  const name = person.name || person.email;
  const toName = recipient ? (recipient.name || recipient.email) : null;
  const happens = mode === 'transfer'
    ? [
      `${money(plan.amount)} leaves ${name}’s available balance and lands in ${toName}’s.`,
      'Two ledger lines are written — TRANSFERRED OUT and TRANSFERRED IN — each carrying your reason.',
      'Both of them get a notification saying the money moved and why.',
      'The audit log records it as critical, under your name.',
      'Nobody is paid out by this. Money only leaves Portal when someone cashes out.',
    ]
    : [
      `${name}’s available balance goes from ${money(plan.from.before)} to ${money(plan.from.after)}.`,
      'One ADJUSTMENT line is written to their ledger with your reason.',
      'They get a notification with the change and the reason.',
      'The audit log records it under your name.',
    ];
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-[20px] bg-[#0A0A0A] px-5 py-4 text-white">
        <div className="font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]">{mode === 'transfer' ? 'TRANSFER' : mode === 'set' ? 'SET BALANCE' : 'ADJUST'}</div>
        <div className="font-mono text-[28px] font-extrabold leading-tight">{mode === 'transfer' ? money(plan.amount) : signedMoney(plan.amount)}</div>
        {mode === 'transfer' && <div className="mt-1 truncate font-mono text-[11px] text-[#C4C4C4]">{name} → {toName}</div>}
      </div>
      <div className="rounded-[20px] bg-[#F7F7F7] px-4 py-1">
        {mode === 'transfer' ? (
          <>
            <Row label={`${name} · AVAILABLE`}>{before2after(plan.from.before, plan.from.after)}</Row>
            <Row label={`${toName} · AVAILABLE`}>{before2after(plan.to.before, plan.to.after)}</Row>
          </>
        ) : (
          <Row label="AVAILABLE">{before2after(plan.from.before, plan.from.after)}</Row>
        )}
        <Row label="ESCROW">UNCHANGED</Row>
        <Row label="REASON"><span className="whitespace-pre-wrap break-words font-normal">“{reason}”</span></Row>
      </div>
      {plan.from.after < 0 && <p className="m-0 rounded-[18px] bg-[#FEE2E2] px-4 py-3 font-mono text-[11px] font-bold text-[#DC2626]">THIS LEAVES {name.toUpperCase()} BELOW ZERO.</p>}
      <div>
        <FieldLabel>WHAT HAPPENS</FieldLabel>
        <ol className="m-0 mt-2 flex list-none flex-col gap-[6px] p-0">
          {happens.map((h, i) => (
            <li key={h} className="flex gap-3 font-mono text-[11px] leading-[1.6]">
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-black text-[9px] font-bold text-white">{i + 1}</span>
              <span>{h}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function DoneList({ mode, result, person, recipient }) {
  const name = person.name || person.email;
  const toName = recipient ? (recipient.name || recipient.email) : null;
  return (
    <div className="flex flex-col gap-3" data-testid="wallet-done">
      <div className="flex items-center gap-3 rounded-[20px] bg-[#ECFDF3] px-4 py-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#15803D] text-white"><ActionIcon name={mode === 'transfer' ? 'transfer' : 'wallet'} size={16} /></span>
        <span className="font-mono text-[12px] font-bold uppercase tracking-[0.1em] text-[#15803D]">
          {mode === 'transfer' ? `MOVED ${money(result.amountCents)}` : `${signedMoney(result.amount)} WRITTEN`}
        </span>
      </div>
      <div className="rounded-[20px] bg-[#F7F7F7] px-4 py-1">
        {mode === 'transfer' ? (
          <>
            <Row label={`${name} · AVAILABLE`}>{before2after(result.fromBalanceBefore, result.fromBalanceAfter)}</Row>
            <Row label={`${toName} · AVAILABLE`}>{before2after(result.toBalanceBefore, result.toBalanceAfter)}</Row>
          </>
        ) : (
          <Row label="AVAILABLE">{before2after(result.balanceBefore, result.balanceAfter)}</Row>
        )}
      </div>
    </div>
  );
}
